import React, { useEffect, useState } from 'react';
import { Spin, Typography, Space, Button } from 'antd';
import { LoadingOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAllDataSources } from '../store/slices/dataSourcesSlice';
import { fetchProjects } from '../store/slices/projectSlice';

const { Title, Text } = Typography;

interface AppInitializerProps {
  children: React.ReactNode;
}

const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [initializationStarted, setInitializationStarted] = useState(false);
  const [initializationComplete, setInitializationComplete] = useState(false);
  
  const { isInitialized: dataSourcesInitialized, isLoading: dataSourcesLoading, dataSources } = useAppSelector(
    (state) => state.dataSources
  );
  const { isLoading: projectsLoading, projects } = useAppSelector((state) => state.project);

  // Debug logging
  console.log('AppInitializer Debug:', {
    initializationStarted,
    initializationComplete,
    dataSourcesInitialized,
    dataSourcesLoading,
    projectsLoading,
    dataSourcesCount: dataSources?.length || 0,
    projectsCount: projects?.length || 0
  });

  // Check if app is fully initialized - simplified logic
  const isAppInitialized = initializationComplete;
  const isLoading = (dataSourcesLoading || projectsLoading) && !initializationComplete;

  useEffect(() => {
    if (!initializationStarted) {
      setInitializationStarted(true);
      
      // Initialize app data
      const initializeApp = async () => {
        try {
          console.log('Starting app initialization...');
          
          // Fetch projects and datasources in parallel
          const [projectsResult, dataSourcesResult] = await Promise.allSettled([
            dispatch(fetchProjects()).unwrap(),
            dispatch(fetchAllDataSources()).unwrap(),
          ]);

          console.log('Initialization results:', { projectsResult, dataSourcesResult });
          
          // Mark initialization as complete regardless of results
          setInitializationComplete(true);
          console.log('App initialization completed');
          
        } catch (error) {
          console.error('App initialization failed:', error);
          // Still mark as complete to avoid infinite loading
          setInitializationComplete(true);
        }
      };

      initializeApp();
    }
  }, [dispatch, initializationStarted]);

  // Fallback: auto-complete after 10 seconds to prevent infinite loading
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (!initializationComplete) {
        console.warn('App initialization taking too long, forcing completion');
        setInitializationComplete(true);
      }
    }, 10000);

    return () => clearTimeout(fallbackTimer);
  }, [initializationComplete]);

  // Show loading screen during initialization
  if (!isAppInitialized || isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f5f5f5',
        }}
      >
        <Space direction="vertical" size="large" style={{ textAlign: 'center' }}>
          <div>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
            />
          </div>
          
          <div>
            <Title level={3} style={{ color: '#1890ff', margin: 0 }}>
              Initializing ThiranAI
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Loading projects and data sources...
            </Text>
          </div>

          <Space>
            <DatabaseOutlined style={{ color: dataSourcesInitialized ? '#52c41a' : '#d9d9d9' }} />
            <Text type={dataSourcesInitialized ? 'success' : 'secondary'}>
              Data Sources {dataSourcesInitialized ? '✓' : '⏳'}
            </Text>
          </Space>

          <div style={{ marginTop: '16px' }}>
            <Button 
              type="link" 
              size="small"
              onClick={() => setInitializationComplete(true)}
            >
              Skip Loading (Development)
            </Button>
          </div>
        </Space>
      </div>
    );
  }

  // Render app once initialization is complete
  return <>{children}</>;
};

export default AppInitializer;